/**
 * "Email Deletion" ticket category: one ticket can carry many mailboxes that
 * should be removed. Same shape of module as `email-creation.ts` — the single
 * source of truth for the row and its validation, used by the web form (instant
 * feedback) and by the API (`createTicketDtoSchema`, the authoritative check).
 *
 * This is a SERVICE REQUEST only: nothing here deletes a mailbox. Values are
 * employee PII; audit rows record counts only and nothing logs them.
 */
import { z } from 'zod';
/** Stable catalogue code of the category (names can be renamed by an admin; codes cannot). */
export const EMAIL_DELETION_CATEGORY_CODE = 'EMAIL_DELETION';
/** Same safety cap as Email Creation: only there so one request body cannot insert unbounded rows. */
export const EMAIL_DELETION_MAX_USERS = 100;
export const EMAIL_DELETION_FIELDS = ['email', 'employeeId'];
export const EMAIL_DELETION_FIELD_LABELS = {
    email: 'Email',
    employeeId: 'Employee ID',
};
export const EMAIL_DELETION_MAX_LENGTHS = {
    email: 254,
    employeeId: 50,
};
const EMAIL_RE = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[^\s@<>()[\]\\,;:"]{2,}$/;
/** Letters and digits plus common separators. Not numeric-only: "E11515" and "GENERIC ID" are both valid. */
const EMPLOYEE_ID_RE = /^[A-Za-z0-9][A-Za-z0-9 ._\-/]*$/;
/** Per-row validation; `{}` when valid. Messages are written for end users. */
export function validateEmailDeletionUser(u) {
    const errors = {};
    const email = (u.email ?? '').trim();
    if (!email)
        errors.email = 'Email is required.';
    else if (email.length > EMAIL_DELETION_MAX_LENGTHS.email || !EMAIL_RE.test(email))
        errors.email = 'Enter a valid email address.';
    const emp = (u.employeeId ?? '').trim();
    if (!emp)
        errors.employeeId = 'Employee ID is required.';
    else if (emp.length > EMAIL_DELETION_MAX_LENGTHS.employeeId)
        errors.employeeId = `Employee ID must be ${EMAIL_DELETION_MAX_LENGTHS.employeeId} characters or fewer.`;
    else if (!EMPLOYEE_ID_RE.test(emp))
        errors.employeeId = 'Use letters, numbers, spaces, dots, dashes or slashes only.';
    return errors;
}
export const EMAIL_DELETION_DUPLICATE_MESSAGE = 'This email account has already been added to the deletion request.';
/**
 * Duplicate rows: the same email address (case-insensitive, trimmed). An exact
 * Email + Employee ID repeat is necessarily included, because it repeats the
 * email. Two mailboxes for one employee ID are legitimate and NOT flagged.
 */
export function findEmailDeletionDuplicates(rows) {
    const out = [];
    const seen = new Map();
    rows.forEach((r, index) => {
        const email = (r.email ?? '').trim().toLowerCase();
        if (!email)
            return;
        const prev = seen.get(email);
        if (prev === undefined)
            seen.set(email, index);
        else
            out.push({ index, field: 'email', duplicateOf: prev });
    });
    return out;
}
/** One mailbox as accepted by `POST /tickets`. Trimmed; field rules come from `validateEmailDeletionUser`. */
export const emailDeletionUserSchema = z
    .object({
    email: z.string().trim().max(EMAIL_DELETION_MAX_LENGTHS.email),
    employeeId: z.string().trim().max(EMAIL_DELETION_MAX_LENGTHS.employeeId),
})
    .strict()
    .superRefine((u, ctx) => {
    for (const [field, message] of Object.entries(validateEmailDeletionUser(u))) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message });
    }
});
/** The list on `POST /tickets`: at least one, no duplicate emails, bounded size. */
export const emailDeletionUsersSchema = z
    .array(emailDeletionUserSchema)
    .min(1)
    .max(EMAIL_DELETION_MAX_USERS)
    .superRefine((rows, ctx) => {
    for (const d of findEmailDeletionDuplicates(rows)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [d.index, d.field], message: EMAIL_DELETION_DUPLICATE_MESSAGE });
    }
});
/** A stored row as returned on ticket detail. */
export const emailDeletionUserRecordSchema = z.object({
    id: z.string().uuid(),
    position: z.number().int().positive(),
    email: z.string(),
    employeeId: z.string(),
});
//# sourceMappingURL=email-deletion.js.map